// components/PropertyDashboard.js
import React, { useEffect, useState,useRef } from "react";

import { AiOutlineClose, AiOutlineArrowRight } from "react-icons/ai";
import { toast } from "react-toastify";

import { baseUrl } from "../../../Environments/environment";
import { useAuth } from "../../../Contexts/AuthContext/AuthContext";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import { useProperty } from "../../../Contexts/PropertyContext/PropertyContext";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../../api";
import { TrashIcon } from "@heroicons/react/24/solid";
import { Trash2Icon } from "lucide-react";

// function classNames(...classes) {
//   return classes.filter(Boolean).join(" ");
// }
const renderStars = (rating) => {
	const full = Math.max(0, Math.min(5, Number(rating) || 0));
	return (
	  <div className="flex items-center gap-1">
		{[...Array(5)].map((_, i) => (
		  <span
			key={i}
			className={
			  i < full ? "text-yellow-400 text-xl" : "text-gray-300 text-xl"
			}
		  >
			★
		  </span>
		))}
	  </div>
	);
  };

const PropertyDashboard = () => {
	const fetchedRatingsRef = useRef(new Set());

	const [deleteConfirmation, setDeleteConfirmation] = useState("");
	const [properties, setProperties] = useState([]);
	const { accessToken, user, logout } = useAuth();
	const { selectProperty } = useProperty();
	const [loading, setLoading] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedProperty, setSelectedProperty] = useState(null);
	const navigate = useNavigate(); // Initialize useNavigate
	useEffect(() => {
		const fetchProperties = async () => {
		  try {
			const res = await apiClient.get(`${baseUrl}/property?slug=true`);
	  
			const props = res.data.result.properties.map((p) => ({
			  ...p,
			  rating: undefined,
			  reviewCount: undefined,
			}));
	  
			setProperties(props);
		  } catch (err) {
			toast.error("Failed to fetch properties");
		  }
		};
	  
		fetchProperties();
	  }, []);
	  useEffect(() => {
		if (!accessToken) return;
	  
		properties.forEach((property) => {
		  if (
			!property.slug ||
			fetchedRatingsRef.current.has(property._id)
		  ) {
			return;
		  }
	  
		  // mark as fetching (prevents re-run)
		  fetchedRatingsRef.current.add(property._id);
	  
		  apiClient
			.get(`${baseUrl}/property/details?place_id=${property.slug}`, {
			  headers: {
				Authorization: `Bearer ${accessToken}`,
			  },
			})
			.then((res) => {
			  setProperties((prev) =>
				prev.map((p) =>
				  p._id === property._id
					? {
						...p,
						rating: res?.data?.result?.rating,
						reviewCount: res?.data?.result?.user_ratings_total,
					  }
					: p
				)
			  );
			})
			.catch(() => {
			  setProperties((prev) =>
				prev.map((p) =>
				  p._id === property._id
					? { ...p, rating: null, reviewCount: null }
					: p
				)
			  );
			});
		});
	  }, [properties, accessToken]);
	  

	// const handleCardClick = (property) => {
	//   selectProperty(property._id);
	//   navigate("/dashboard");
	// };

	const handleCardClick = async (property) => {
		const { _id: propertyId } = property;
		try {
			selectProperty(propertyId);
			navigate("/dashboard");
		} catch (error) {
			const errorMessage =
				error.response?.data?.message || "Error checking Twilio setup:";
			toast.error(errorMessage);
			// Handle error (e.g., show a notification)
		}
	};

	const handleLogout = async () => {
		try {
			await logout();
			navigate("/"); // Navigate to homepage after logout
		} catch (error) {
			console.error("Failed to log out:", error);
		}
	};

	const openModal = (property) => {
		setSelectedProperty(property);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setSelectedProperty(null);
		setIsModalOpen(false);
	};
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [propertyIdToDelete, setPropertyIdToDelete] = useState(null);
	const openDeletePropertyModal = (propertyId) => {
		setIsDeleteModalOpen(true);
		setPropertyIdToDelete(propertyId);
	};
	const deleteProperty = async (e, propertyId) => {
		e.stopPropagation(); // Prevent triggering card click
		try {
			setLoading(true);
			const response = await apiClient.delete(
				`${baseUrl}/property/${propertyId}`,
			);
			const updatedProperties = properties.filter(
				(property) => property._id !== propertyId,
			);
			setProperties(updatedProperties);
			toast.success("Property deleted successfully");
		} catch (error) {
			const errorMessage =
				error.response?.data?.message || "Failed to delete property.";
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			{loading && <LoadingSpinner />}
			<div className="min-h-screen bg-gray-300">
				<div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
					{/* Left side */}
					<div className="flex flex-col items-center justify-start p-4 md:p-6 m-4 md:m-6 lg:m-20 mt-8 md:mt-20 lg:mt-40 space-y-4 md:space-y-6">
						<div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-full bg-gray-100 flex items-center justify-center">
							{/* <UsersIcon className="text-6xl text-gray-700" /> */}
							<h1 className="text-base md:text-lg lg:text-xl font-bold">
								Logo
							</h1>
						</div>
						<h2 className="text-lg md:text-xl font-semibold">Welcome Again!</h2>
						<button
							onClick={handleLogout}
							className="text-white bg-red-500 hover:bg-red-600 px-3 py-1.5 md:px-4 md:py-2 rounded-md text-sm md:text-base w-full sm:w-auto"
						>
							Logout
						</button>
					</div>

					{/* Right side */}
					<div className="md:col-span-3 mt-4 md:mt-8 lg:mt-20 mx-4 md:mx-5 lg:mr-10 lg:ml-5">
						<div className="flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-0">
							<div>
								<h3 className="text-base md:text-lg font-semibold leading-6 text-gray-900">
									Listings assigned to you
								</h3>
								<p className="mt-1 md:mt-2 text-xs md:text-sm text-gray-600">
									Select one to proceed
								</p>
							</div>
							{user.role === "admin" && (
								<button className="bg-indigo-600 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-md shadow hover:bg-indigo-700 text-sm md:text-base w-full sm:w-auto">
									<a href="/addproperty">Onboard New Listing</a>
								</button>
							)}
						</div>

						<dl className="mt-4 md:mt-5 grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
							{properties.filter(Boolean).map((property) => (
								<div
									key={property._id}
									className="relative flex flex-col bg-white rounded-lg shadow-lg overflow-hidden w-full max-w-full sm:max-w-sm mx-auto"
								>
									<div
										className="flex-1 p-3 md:p-4 cursor-pointer"
										onClick={() => handleCardClick(property)}
									>
										<div className="flex items-start sm:items-center">
											<img
												src={property.logoUrl}
												alt={`${property.name} logo`}
												className="h-10 w-10 sm:h-12 sm:w-12 rounded-full border-2 border-indigo-500 flex-shrink-0"
											/>
											<div className="ml-3 md:ml-4 flex-1 min-w-0 space-y-1">
												<h3 className="flex items-center justify-between text-sm md:text-base font-semibold text-gray-900 cursor-pointer truncate">
													{property.name}
													<Trash2Icon
														className="text-red-500 hover:text-red-700 cursor-pointer w-5 h-5"
														onClick={(e) => {
															e.stopPropagation();
															openDeletePropertyModal(property._id);
														}}
													/>
												</h3>
												<p className="text-xs text-gray-500">
													Country: {property.country}
												</p>
												<p className="text-xs text-gray-500 truncate">
													Address: {property.address}
												</p>
											</div>
										</div>
									</div>
									<div className=" text-sm">
  {property.rating === undefined ? (
    <div className="h-4 w-full bg-gray-200 animate-pulse rounded " />
  ) : property.rating ? (
	<div className="flex items-center gap-2 pr-3 justify-end">
                      <span className="text-sm text-gray-700 font-semibold ">
                        {Number(property.rating ?? 0)}
                      </span>
                      {renderStars(property.rating ?? 0)}  <span className="text-gray-500 text-xs">
					    {property.reviewCount} Google reviews 
      </span>
                    </div>
  
  ) : (
    <span className="text-gray-400 flex items-center gap-2 pr-3 justify-end">No reviews</span>
  )}
</div>

									{/* Actions */}
									<div className="bg-gray-50 px-3 md:px-4 py-3 md:py-4 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 sm:gap-0">
										<button
											onClick={() => openModal(property)}
											className="text-xs sm:text-sm font-medium text-indigo-600 hover:text-indigo-500 text-center sm:text-left"
										>
											View Full Details
										</button>
										<div
											// href={`mailto:${property.email}`}  {/* Contact via email */}
											className="text-xs sm:text-sm font-medium text-indigo-600 hover:text-indigo-500 text-center sm:text-left cursor-pointer"
										>
											Add User
										</div>
										{/* Arrow button new code change */}
										{/* <button
											onClick={() => handleCardClick(property)}
											className="text-indigo-600 hover:text-indigo-500 transition-transform transform hover:scale-110"
										>
											<AiOutlineArrowRight size={20} />
										</button> */}
									</div>
								</div>
							))}
						</dl>
					</div>
				</div>
				{isDeleteModalOpen && (
					<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
						<div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
							<div className="p-6">
								<h2 className="text-xl font-semibold text-gray-900">
									Confirm Deletion
								</h2>

								<p className="mt-3 text-sm text-gray-600">
									This action is{" "}
									<span className="font-medium text-red-600">permanent</span>.
									Once deleted, this property cannot be recovered.
								</p>

								<div className="mt-5">
									<label
										htmlFor="delete-confirm"
										className="block text-sm font-medium text-gray-700"
									>
										Type <span className="font-semibold">delete</span> to
										confirm
									</label>

									<input
										id="delete-confirm"
										type="text"
										value={deleteConfirmation}
										onChange={(e) => setDeleteConfirmation(e.target.value)}
										placeholder="delete"
										className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
										autoFocus
									/>
								</div>
							</div>

							<div className="flex items-center justify-end gap-3 border-t bg-gray-50 px-6 py-4">
								<button
									onClick={() => setIsDeleteModalOpen(false)}
									className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
								>
									Cancel
								</button>

								<button
									disabled={deleteConfirmation !== "delete"}
									onClick={(e) => {
										deleteProperty(e, propertyIdToDelete);
										setIsDeleteModalOpen(false);
										setDeleteConfirmation("");
									}}
									className={`rounded-md px-4 py-2 text-sm font-medium text-white transition
            ${
							deleteConfirmation === "delete"
								? "bg-red-600 hover:bg-red-700"
								: "cursor-not-allowed bg-red-300"
						}`}
								>
									Delete Property
								</button>
							</div>
						</div>
					</div>
				)}

				{isModalOpen && selectedProperty && (
					<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
						<div className="bg-white p-4 sm:p-6 md:p-8 rounded-lg shadow-xl w-full max-w-[90vw] sm:max-w-[80vw] md:max-w-[60vw] lg:max-w-[40vw] xl:max-w-[30vw] overflow-y-auto max-h-[90vh]">
							<div className="flex justify-between items-center mb-4 md:mb-6">
								<h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">
									Property Details
								</h2>
								<button
									onClick={closeModal}
									className="text-gray-600 hover:text-gray-900 focus:outline-none flex-shrink-0"
								>
									<AiOutlineClose size={20} className="sm:w-6 sm:h-6" />
								</button>
							</div>
							<div className="space-y-4 md:space-y-6">
								<div className="space-y-1">
									<label className="block text-xs sm:text-sm font-medium text-gray-700">
										Name
									</label>
									<p className="text-sm sm:text-base text-gray-800 break-words">
										{selectedProperty.name}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-xs sm:text-sm font-medium text-gray-700">
										Email
									</label>
									<p className="text-sm sm:text-base text-gray-800 break-words">
										{selectedProperty.email}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-xs sm:text-sm font-medium text-gray-700">
										Phone Number
									</label>
									<p className="text-sm sm:text-base text-gray-800 break-words">
										{selectedProperty.countryCode}
										{selectedProperty.phoneNumber}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-xs sm:text-sm font-medium text-gray-700">
										Website
									</label>
									<p className="text-sm sm:text-base text-gray-800 break-words">
										{selectedProperty.website}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-xs sm:text-sm font-medium text-gray-700">
										About
									</label>
									<p className="text-sm sm:text-base text-gray-800 break-words">
										{selectedProperty.about || "N/A"}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-xs sm:text-sm font-medium text-gray-700">
										Country
									</label>
									<p className="text-sm sm:text-base text-gray-800 break-words">
										{selectedProperty.country}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-xs sm:text-sm font-medium text-gray-700">
										State
									</label>
									<p className="text-sm sm:text-base text-gray-800 break-words">
										{selectedProperty.state}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-xs sm:text-sm font-medium text-gray-700">
										City
									</label>
									<p className="text-sm sm:text-base text-gray-800 break-words">
										{selectedProperty.city}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-xs sm:text-sm font-medium text-gray-700">
										Address
									</label>
									<p className="text-sm sm:text-base text-gray-800 break-words">
										{selectedProperty.address}
									</p>
								</div>

								<div className="space-y-1">
									<label className="block text-xs sm:text-sm font-medium text-gray-700">
										Active
									</label>
									<p className="text-sm sm:text-base text-gray-800 break-words">
										{selectedProperty.active ? "Yes" : "No"}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-xs sm:text-sm font-medium text-gray-700">
										Created At
									</label>
									<p className="text-sm sm:text-base text-gray-800 break-words">
										{new Date(selectedProperty.createdAt).toLocaleString()}
									</p>
								</div>
								<div className="space-y-1">
									<label className="block text-xs sm:text-sm font-medium text-gray-700">
										Updated At
									</label>
									<p className="text-sm sm:text-base text-gray-800 break-words">
										{new Date(selectedProperty.updatedAt).toLocaleString()}
									</p>
								</div>
								{/* Add more property details here */}
							</div>
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export default PropertyDashboard;
